import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  FiltersDefinition,
  OnBulkActionCallback,
  OneDataCollection,
} from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  CompanyPerDiemRatesDataCollectionQuery,
  CompanyPerDiemRatesPageDocument,
  CompanyPerDiemRatesDataCollectionDocument,
  UpsertCompanyPerDiemRatesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { countryName } from 'modules/core/lib/countryList'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import OverridePerDiemRateModal from './OverridePerDiemRateModal'

type RateNode = Omit<
  CompanyPerDiemRatesDataCollectionQuery['expenses']['officialPerDiemRatesConnection']['nodes'][number],
  'id'
> & {
  id: string
}

const CompanyPerDiemRatesList = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const { data } = useQuery(CompanyPerDiemRatesPageDocument, {
    variables: {
      companyId: company.id,
    },
  })
  const canCustomizeSettings = data?.expenses._policies.expenseCustomizeSettings.can ?? false
  const hasCompanyPerDiemRatesFeature =
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === 'dev_expenses_germany_per_diem_rates' && feature.enabled
    ) ?? false
  const [selectedRates, setSelectedRates] = React.useState<RateNode[]>([])
  const [isOverrideModalOpen, setIsOverrideModalOpen] = React.useState(false)

  const [upsertCompanyPerDiemRates] = useMutation(UpsertCompanyPerDiemRatesDocument, {
    extractorFn: (response) => response.expensesMutations.upsertExpensesCompanyPerDiemRate,
    refetchQueries: [CompanyPerDiemRatesDataCollectionDocument],
  })

  const dataSource = useGraphqlDataCollectionSource({
    document: CompanyPerDiemRatesDataCollectionDocument,
    mapData: (collectionData) =>
      collectionData.expenses.officialPerDiemRatesConnection.nodes.filter(
        (node): node is RateNode => Boolean(node.id)
      ),
    mapVariables: ({ search, sortings }) => ({
      companyId: company.id,
      search,
      sortOrder: sortings?.[0],
    }),
    paginationType: 'infinite-scroll',
    perPage: 25,
    mapPagination: (collectionData) => ({
      total: collectionData.expenses.officialPerDiemRatesConnection.totalCount ?? 0,
      cursor: collectionData.expenses.officialPerDiemRatesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage:
        collectionData.expenses.officialPerDiemRatesConnection.pageInfo.hasNextPage ?? false,
    }),
    search: {
      enabled: true,
    },
    sortings: {
      destination_country: {
        label: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.country'),
      },
    },
    defaultSortings: {
      field: 'destination_country',
      order: 'asc',
    },
    selectable:
      canCustomizeSettings && hasCompanyPerDiemRatesFeature ? (item: RateNode) => item.id : undefined,
    bulkActions:
      canCustomizeSettings && hasCompanyPerDiemRatesFeature
        ? (selectedItems) => {
            const checkedItems = selectedItems.itemsStatus
              .filter((itemStatus) => itemStatus.checked)
              .map((itemStatus) => itemStatus.item)

            if (!checkedItems.length) return { primary: [] }

            return {
              primary: [
                {
                  id: 'override-rates',
                  label: i18n.t('expenses_v2.settings.rate_configurations.override_modal.bulk_action'),
                  icon: Pencil,
                },
              ],
            }
          }
        : undefined,
    itemActions:
      canCustomizeSettings && hasCompanyPerDiemRatesFeature
        ? (item: RateNode) => [
            {
              label: i18n.t('expenses_v2.settings.rate_configurations.override_modal.row_action'),
              icon: Pencil,
              onClick: () => {
                setSelectedRates([item])
                setIsOverrideModalOpen(true)
              },
            },
          ]
        : undefined,
  })

  const closeOverrideModal = () => {
    setIsOverrideModalOpen(false)
    setSelectedRates([])
  }

  const handleBulkAction = (
    action: string,
    items: Parameters<OnBulkActionCallback<RateNode, FiltersDefinition<string>>>[1]
  ) => {
    if (action !== 'override-rates') return

    const checkedItems = items.itemsStatus
      .filter((itemStatus) => itemStatus.checked)
      .map((itemStatus) => itemStatus.item)
    setSelectedRates(checkedItems)
    setIsOverrideModalOpen(true)
  }

  const handleConfirmOverride = async ({
    overrideMode,
    overrideValue,
  }: {
    overrideMode: 'multiplier' | 'percentage' | 'bonus'
    overrideValue: number
  }) => {
    const result = await upsertCompanyPerDiemRates({
      variables: {
        companyId: company.id,
        officialPerDiemRateIds: selectedRates.map((rate) => rate.id),
        overrideMode,
        overrideValue,
      },
    })
    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
      closeOverrideModal()
    }
  }

  const columns = [
    {
      id: 'destination_country',
      label: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.country'),
      render: (item: RateNode) => ({
        type: 'text' as const,
        value: countryName(item.destinationCountry, locale),
      }),
      sorting: 'destination_country' as const,
    },
    {
      id: 'destination',
      label: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.destination_country'),
      render: (item: RateNode) => ({
        type: 'text' as const,
        value: item.locality ?? '',
      }),
    },
    {
      id: 'amountFullCents',
      label: i18n.t('expenses_v2.settings.rate_configurations.datatable.fields.amount_full_official'),
      render: (item: RateNode) => ({
        type: 'text' as const,
        value: amountWithCurrency(Number(item.amountFullCents), item.currency, locale),
      }),
    },
    ...(hasCompanyPerDiemRatesFeature
      ? [
          {
            id: 'companyAmountFullCents',
            label: i18n.t(
              'expenses_v2.settings.rate_configurations.datatable.fields.amount_full_custom'
            ),
            render: (item: RateNode) => ({
              type: 'text' as const,
              value: item.activeCompanyPerDiemRate
                ? amountWithCurrency(
                    Number(item.activeCompanyPerDiemRate.amountFullCents),
                    item.currency,
                    locale
                  )
                : '--',
            }),
          },
        ]
      : []),
    {
      id: 'amountPartialCents',
      label: i18n.t(
        'expenses_v2.settings.rate_configurations.datatable.fields.amount_partial_official'
      ),
      render: (item: RateNode) => ({
        type: 'text' as const,
        value: amountWithCurrency(Number(item.amountPartialCents), item.currency, locale),
      }),
    },
    ...(hasCompanyPerDiemRatesFeature
      ? [
          {
            id: 'companyAmountPartialCents',
            label: i18n.t(
              'expenses_v2.settings.rate_configurations.datatable.fields.amount_partial_custom'
            ),
            render: (item: RateNode) => ({
              type: 'text' as const,
              value: item.activeCompanyPerDiemRate
                ? amountWithCurrency(
                    Number(item.activeCompanyPerDiemRate.amountPartialCents),
                    item.currency,
                    locale
                  )
                : '--',
            }),
          },
        ]
      : []),
    {
      id: 'amountOvernightCents',
      label: i18n.t(
        'expenses_v2.settings.rate_configurations.datatable.fields.amount_overnight_official'
      ),
      render: (item: RateNode) => ({
        type: 'text' as const,
        value: amountWithCurrency(Number(item.amountOvernightCents), item.currency, locale),
      }),
    },
    ...(hasCompanyPerDiemRatesFeature
      ? [
          {
            id: 'companyAmountOvernightCents',
            label: i18n.t(
              'expenses_v2.settings.rate_configurations.datatable.fields.amount_overnight_custom'
            ),
            render: (item: RateNode) => ({
              type: 'text' as const,
              value: item.activeCompanyPerDiemRate
                ? amountWithCurrency(
                    Number(item.activeCompanyPerDiemRate.amountOvernightCents),
                    item.currency,
                    locale
                  )
                : '--',
            }),
          },
        ]
      : []),
  ]

  return (
    <Box paddingX="s12" style={{ height: '100%', overflow: 'auto' }}>
      {isOverrideModalOpen ? (
        <OverridePerDiemRateModal
          selectedRates={selectedRates}
          onClose={closeOverrideModal}
          onConfirm={handleConfirmOverride}
        />
      ) : null}
      <OneDataCollection
        id="company-per-diem-rates/v4"
        storage={{
          features: ['sortings', 'search'],
        }}
        fullHeight
        emptyStates={{
          'no-data': {
            title: i18n.t('common.no_data_available'),
            description: '',
          },
        }}
        source={dataSource}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnHiding: true,
              allowColumnReordering: true,
              columns,
            },
          },
        ]}
      />
    </Box>
  )
}

export default CompanyPerDiemRatesList
